import React from 'react'
import {
	EuiButton,
	EuiFlexGroup,
	EuiFlexItem,
	EuiIcon,
	EuiText,
	EuiTextColor,
} from '@equipmentshare/ds2'
import { AddLineIcon, FileTextLineIcon } from '@equipmentshare/ds2-icons'
import TransferOrderLineItemForm from './TransferOrderLineItemForm'
import { TransferOrderLineItem } from '../hooks/useTransferOrderForm'

const COLUMN_WIDTHS = {
	quantity: 90,
	itemIsFor: 220,
	noteButton: 120,
	deleteButton: 40,
} as const

export type TransferOrderLineItemsListProps = {
	lineItems: TransferOrderLineItem[],
	onAddLineItem: () => void,
	onRemoveLineItem: ( index: number ) => void,
	onUpdateLineItem: ( index: number, updates: Partial<TransferOrderLineItem> ) => void,
	hasValidationErrors?: boolean,
	validationErrorMessage?: string,
	showValidationErrors?: boolean,
	branchId?: string,
}

export default function TransferOrderLineItemsList( {
	lineItems,
	onAddLineItem,
	onRemoveLineItem,
	onUpdateLineItem,
	hasValidationErrors,
	validationErrorMessage,
	showValidationErrors = false,
	branchId,
}: TransferOrderLineItemsListProps ) {
	return (
		<EuiFlexGroup direction="column" gutterSize="m">
			{/* Section Title */}
			<EuiFlexItem grow={false}>
				<EuiFlexGroup gutterSize="s" alignItems="center">
					<EuiFlexItem grow={false}>
						<EuiIcon type={FileTextLineIcon} size="m" />
					</EuiFlexItem>
					<EuiFlexItem grow={false}>
						<strong>Items</strong>
					</EuiFlexItem>
				</EuiFlexGroup>
			</EuiFlexItem>

			{/* General validation error */}
			{showValidationErrors && hasValidationErrors && validationErrorMessage && (
				<EuiFlexItem grow={false}>
					<EuiText color="danger">
						<strong>Error:</strong> {validationErrorMessage}
					</EuiText>
				</EuiFlexItem>
			)}

			{/* Column Headers */}
			<EuiFlexItem grow={false}>
				<EuiFlexGroup direction="row" gutterSize="s" alignItems="center" wrap={false}>
					<EuiFlexItem>
						<EuiText size="xs" style={{ fontWeight: 600 }}>
							Item <EuiTextColor color="danger">*</EuiTextColor>
						</EuiText>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.quantity }}>
						<EuiText size="xs" style={{ fontWeight: 600 }}>
							Qty <EuiTextColor color="danger">*</EuiTextColor>
						</EuiText>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.itemIsFor }}>
						<EuiText size="xs" style={{ fontWeight: 600 }}>Item is for</EuiText>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.noteButton }} />
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.deleteButton }} />
				</EuiFlexGroup>
			</EuiFlexItem>

			{/* Line Items */}
			<EuiFlexItem>
				<EuiFlexGroup direction="column" gutterSize="s">
					{lineItems.map( ( lineItem, index ) => (
						<EuiFlexItem key={lineItem.id}>
							<TransferOrderLineItemForm
								lineItem={lineItem}
								index={index}
								onUpdate={onUpdateLineItem}
								onRemove={onRemoveLineItem}
								isOnlyItem={lineItems.length === 1}
								showValidationErrors={showValidationErrors}
								branchId={branchId}
							/>
						</EuiFlexItem>
					) )}
				</EuiFlexGroup>
			</EuiFlexItem>

			{/* Add Item Button */}
			<EuiFlexItem grow={false} style={{ alignSelf: 'flex-start' }}>
				<EuiButton
					iconType={AddLineIcon}
					onClick={onAddLineItem}
					color="primary"
					fill={false}
					data-testid="add-line-item-button"
				>
					Add Item
				</EuiButton>
			</EuiFlexItem>
		</EuiFlexGroup>
	)
}
